// Chart Management Interface Component
// Architecture: v4.0.0 Multiple Charts Implementation
// Provides recent charts loading, search/filtering functionality

import { devError } from '../../../utils/devLog';
import { getPlaceName } from '../../../utils/apiDataTransformers';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  IconButton,
  Spinner,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex
} from '@chakra-ui/react';
import { ChevronDownIcon, TimeIcon, StarIcon, DeleteIcon, DownloadIcon } from '@chakra-ui/icons';
import { useMultipleCharts } from './MultipleChartsContext';
import { ChartMetadata } from '../../../models/storage';
import { getStorageService } from '../../../services/StorageService';

/**
 * Convert decimal degrees to DMS format
 */
const formatCoordinateDMS = (decimal: number, isLatitude: boolean): string => {
  const direction = isLatitude 
    ? (decimal >= 0 ? 'N' : 'S') 
    : (decimal >= 0 ? 'E' : 'W');
  
  const abs = Math.abs(decimal);
  const degrees = Math.floor(abs);
  const minutes = Math.floor((abs - degrees) * 60);
  const seconds = Math.floor(((abs - degrees) * 60 - minutes) * 60);
  
  return `${degrees} ${direction} ${minutes.toString().padStart(2, '0')}' ${seconds.toString().padStart(2, '0')}"`;
};

/**
 * Format timezone for display
 */
const formatTimezone = (timezone: string): string => {
  // Handle formats like "3:00:00" or "+3:00:00" or "-5:00:00"
  if (timezone.includes(':')) {
    let formatted = timezone;
    if (!timezone.startsWith('+') && !timezone.startsWith('-')) {
      formatted = `+${timezone}`;
    }
    return `GMT${formatted.substring(0, 6)}`; // Take only hours:minutes part
  }
  return `GMT${timezone.startsWith('+') || timezone.startsWith('-') ? timezone : `+${timezone}`}`;
};

/**
 * Trim long strings for display
 */
const trimString = (str: string, maxLength: number = 25): string => {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
};

export interface RecentChart {
  chartId: string;
  metadata: ChartMetadata;
  lastAccessed: Date;
  isFavorite?: boolean;
  previewData?: {
    planetCount: number;
    chartType: string;
  };
  coordinates?: {
    latitude: number;
    longitude: number;
    latitudeDMS?: string;
    longitudeDMS?: string;
  };
  timeInfo?: {
    time: string;
    timezone: string;
    location?: string;
    date?: string;
  };
}

export interface ChartManagementProps {
  isOpen: boolean;
  onClose: () => void;
  maxRecentCharts?: number;
}

/**
 * Chart Management Interface
 * Provides recent charts loading, search, and management functionality
 */
export const ChartManagement: React.FC<ChartManagementProps> = ({
  isOpen,
  onClose,
  maxRecentCharts = 20
}) => {
  const {
    loadedChartIds,
    activeChartId,
    loadChart: loadChartIntoContext,
    switchToChart,
    unloadChart
  } = useMultipleCharts();

  const [recentCharts, setRecentCharts] = useState<RecentChart[]>([]);
  const [filteredCharts, setFilteredCharts] = useState<RecentChart[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'lastAccessed' | 'name' | 'location'>('lastAccessed');
  const [filterBy, setFilterBy] = useState<'all' | 'favorites' | 'loaded'>('all');
  const toast = useToast();

  /**
   * Load recent charts from storage
   */
  const loadRecentCharts = useCallback(async () => {
    setIsLoading(true);
    try {
      const storageService = getStorageService();
      const result = await storageService.getRecentCharts();

      if (result.success && result.data) {
        const recentChartsData = await Promise.all(
          result.data.slice(0, maxRecentCharts).map(async (chart: any) => {
            // Try to load full chart data to get coordinates and time info
            let coordinates;
            let timeInfo;
            let basicInfo; // Move outside try block for broader scope
            try {
              const fullChartResult = await storageService.loadChart(chart.id);
              if (fullChartResult.success && fullChartResult.data?.chartData?.apiResponse?.basic_info) {
                basicInfo = fullChartResult.data.chartData.apiResponse.basic_info;
                
                // Extract coordinates
                const lat = basicInfo.latitudeValue;
                const lon = basicInfo.longitudeValue;
                if (lat !== undefined && lon !== undefined) {
                  coordinates = {
                    latitude: lat,
                    longitude: lon,
                    latitudeDMS: formatCoordinateDMS(lat, true),
                    longitudeDMS: formatCoordinateDMS(lon, false)
                  };
                }
                
                // Extract time, timezone, location, and date
                timeInfo = {
                  time: basicInfo.time || chart.metadata?.chartDateTime?.time || '12:00:00',
                  timezone: basicInfo.timezone || chart.metadata?.chartDateTime?.timezone || 'UTC',
                  location: getPlaceName(basicInfo, chart.metadata?.chartDateTime?.location || 'Unknown Location'),
                  date: basicInfo.date || chart.metadata?.chartDateTime?.date || new Date().toISOString().split('T')[0]
                };
              }
            } catch (e) {
              // Silently fail loading, chart will still show without coordinates/time
            }

            const chartDateTime = {
              date: timeInfo?.date || (chart.datetime ? new Date(chart.datetime).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]),
              time: timeInfo?.time || (chart.datetime ? new Date(chart.datetime).toTimeString().split(' ')[0] : '12:00:00'),
              timezone: timeInfo?.timezone || chart.timezone || 'UTC',
              location: timeInfo?.location || chart.location || 'Unknown Location',
              originalDateStr: chart.originalDateStr,
              originalTimeStr: chart.originalTimeStr
            };

            const metadata = {
              id: chart.id,
              name: chart.name || 'Unnamed Chart',
              created: new Date(chart.createdAt || chart.created || Date.now()),
              modified: new Date(chart.lastModified || chart.modified || Date.now()),
              accessed: new Date(chart.lastAccessed || chart.accessed || Date.now()),
              size: chart.size || 0,
              version: chart.version || 1,
              tags: chart.tags || [],
              description: chart.description || '',
              chartDateTime
            };

            return {
              chartId: chart.id,
              metadata, // metadata.name is now always up-to-date
              lastAccessed: new Date(chart.lastAccessed || chart.createdAt || Date.now()),
              isFavorite: chart.isFavorite || false,
              previewData: {
                planetCount: chart.previewData?.planetCount || 9,
                chartType: chart.chartType || 'natal'
              },
              coordinates,
              timeInfo
            };
          })
        );

        setRecentCharts(recentChartsData);
      } else {
        setRecentCharts([]);
      }
    } catch (error) {
      devError('Failed to load recent charts:', error);
      toast({
        title: 'Load Error',
        description: 'Failed to load recent charts',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [maxRecentCharts, toast]);

  /**
   * Filter and sort charts based on current filters
   */
  const applyFilters = useCallback(() => {
    let filtered = [...recentCharts];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        chart =>
          chart.metadata.name.toLowerCase().includes(query) ||
          (chart.metadata.chartDateTime?.location || '').toLowerCase().includes(query)
      );
    }

    // Apply category filter
    switch (filterBy) {
      case 'favorites':
        filtered = filtered.filter(chart => chart.isFavorite);
        break;
      case 'loaded':
        filtered = filtered.filter(chart => loadedChartIds.includes(chart.chartId));
        break;
      // 'all' - no additional filtering
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.metadata.name.localeCompare(b.metadata.name);
        case 'location':
          return (a.metadata.chartDateTime?.location || '').localeCompare(b.metadata.chartDateTime?.location || '');
        case 'lastAccessed':
        default:
          return b.lastAccessed.getTime() - a.lastAccessed.getTime();
      }
    });

    setFilteredCharts(filtered);
  }, [recentCharts, searchQuery, sortBy, filterBy, loadedChartIds]);

  /**
   * Load chart from recent charts
   */
  const handleLoadChart = useCallback(async (recentChart: RecentChart) => {
    try {
      setIsLoading(true);
      
      // Check if chart is already loaded
      if (loadedChartIds.includes(recentChart.chartId)) {
        switchToChart(recentChart.chartId);
        onClose();
        return;
      }

      // Load chart using context's loadChart (it loads from storage internally)
      await loadChartIntoContext(recentChart.chartId, { makeActive: true });
      
      toast({
        title: 'Chart Loaded',
        description: `"${recentChart.metadata.name}" loaded successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
      
      onClose();
    } catch (error) {
      devError('Failed to load chart:', error);
      toast({
        title: 'Load Error',
        description: `Failed to load chart "${recentChart.metadata.name}"`,
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  }, [loadedChartIds, switchToChart, loadChartIntoContext, onClose, toast]);

  /**
   * Toggle favorite status
   */
  const handleToggleFavorite = useCallback(async (chartId: string) => {
    try {
      const storageService = getStorageService();
      const chartIndex = recentCharts.findIndex(chart => chart.chartId === chartId);
      
      if (chartIndex >= 0) {
        const updatedChart = {
          ...recentCharts[chartIndex],
          isFavorite: !recentCharts[chartIndex].isFavorite
        };

        // Update local state
        const updatedCharts = [...recentCharts];
        updatedCharts[chartIndex] = updatedChart;
        setRecentCharts(updatedCharts);

        // Update storage
        await storageService.updateChartMetadata(chartId, {
          isFavorite: updatedChart.isFavorite
        });
      }
    } catch (error) {
      devError('Failed to toggle favorite:', error);
    }
  }, [recentCharts]);

  /**
   * Delete chart from recent charts
   */
  const handleDeleteChart = useCallback(async (chartId: string) => {
    try {
      const storageService = getStorageService();
      await storageService.deleteChart(chartId);
      
      setRecentCharts(prev => prev.filter(chart => chart.chartId !== chartId));
      
      // Also unload if currently loaded
      if (loadedChartIds.includes(chartId)) {
        unloadChart(chartId);
      }

      toast({
        title: 'Chart Deleted',
        description: 'Chart removed from recent charts',
        status: 'info',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      devError('Failed to delete chart:', error);
      toast({
        title: 'Delete Error',
        description: 'Failed to delete chart',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  }, [loadedChartIds, unloadChart, toast]);

  // Load recent charts on component mount
  useEffect(() => {
    if (isOpen) {
      loadRecentCharts();
    }
  }, [isOpen, loadRecentCharts]);

  // Apply filters when dependencies change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.400"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={2000}
      onClick={onClose}
    >
      <Box
        bg="white"
        border="1px solid #e5e5e5"
        borderRadius="4px"
        p={4}
        w="580px"
        maxH="75vh"
        fontSize="16px"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <HStack justify="space-between" mb={3} pb={2} borderBottom="1px solid #e0e0e0">
          <Text fontSize="16px" fontWeight="bold" color="#87532d">
            Chart Management
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </HStack>

        {/* Search and Filters */}
        <VStack spacing={2} mb={3}>
          <Input
            placeholder="Search charts by name or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="sm"
            borderColor="#e5e5e5"
            fontSize="16px"
          />
          
          <HStack w="100%" justify="space-between" fontSize="14px">
            <HStack spacing={2}>
              <Text color="#666">Sort by:</Text>
              <Menu>
                <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} fontSize="14px" borderColor="#e5e5e5">
                  {sortBy === 'lastAccessed' ? 'Recent' : 
                   sortBy === 'name' ? 'Name' : 'Location'}
                </MenuButton>
                <MenuList fontSize="14px">
                  <MenuItem onClick={() => setSortBy('lastAccessed')}>Recent</MenuItem>
                  <MenuItem onClick={() => setSortBy('name')}>Name</MenuItem>
                  <MenuItem onClick={() => setSortBy('location')}>Location</MenuItem>
                </MenuList>
              </Menu>
            </HStack>

            <HStack spacing={2}>
              <Text color="#666">Filter:</Text>
              <Menu>
                <MenuButton as={Button} size="xs" variant="outline" rightIcon={<ChevronDownIcon />} fontSize="14px" borderColor="#e5e5e5">
                  {filterBy === 'all' ? 'All Charts' :
                   filterBy === 'favorites' ? 'Favorites' : 'Loaded'}
                </MenuButton>
                <MenuList fontSize="14px">
                  <MenuItem onClick={() => setFilterBy('all')}>All Charts</MenuItem>
                  <MenuItem onClick={() => setFilterBy('favorites')}>Favorites</MenuItem>
                  <MenuItem onClick={() => setFilterBy('loaded')}>Currently Loaded</MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </HStack>
        </VStack>

        {/* Charts List */}
        <Box maxH="360px" overflowY="auto" border="1px solid #e5e5e5" borderRadius="2px" bg="#fafafa">
          {isLoading ? (
            <Flex justify="center" p={6}>
              <Spinner size="sm" />
            </Flex>
          ) : filteredCharts.length === 0 ? (
            <Box textAlign="center" p={6} color="#666" fontSize="16px">
              <Text>No charts found</Text>
            </Box>
          ) : (
            <VStack spacing={1} align="stretch" p={2}>
              {filteredCharts.map((chart) => {
                const isLoaded = loadedChartIds.includes(chart.chartId);
                const isActive = activeChartId === chart.chartId;

                return (
                  <Box
                    key={chart.chartId}
                    p={2}
                    border="1px solid #e5e5e5"
                    borderRadius="2px"
                    bg={isActive ? '#e3f2fd' : isLoaded ? '#f1f8e9' : 'white'}
                    _hover={{ bg: isActive ? '#bbdefb' : '#f8f9fa', borderColor: '#ccc' }}
                    transition="all 0.2s ease"
                  >
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={0} flex={1}>
                        <HStack spacing={2} align="center">
                          <Text fontWeight="normal" fontSize="16px" color="#681515" minW="120px">
                            {trimString(chart.metadata.name, 30)}
                          </Text>
                          
                          {chart.isFavorite && (
                            <StarIcon color="#ffc107" boxSize="10px" />
                          )}
                          
                          {isLoaded && (
                            <Text fontSize="11px" bg={isActive ? '#1976d2' : '#4caf50'} color="white" px={1} borderRadius="2px">
                              {isActive ? 'Active' : 'Loaded'}
                            </Text>
                          )}
                        </HStack>
                        
                        <Text fontSize="14px" color="#666">
                          {trimString(chart.metadata.chartDateTime?.location || 'Unknown Place', 30)}
                        </Text>
                        
                        <VStack align="start" spacing={0} fontSize="11px" color="#999">
                          <HStack spacing={1}>
                            <TimeIcon boxSize="10px" />
                            <Text>{chart.timeInfo?.date || chart.metadata.chartDateTime?.date || chart.lastAccessed.toLocaleDateString()}</Text>
                            <Text>•</Text>
                            <Text>{chart.timeInfo?.time || chart.metadata.chartDateTime?.time || 'Unknown Time'}</Text>
                            {chart.timeInfo?.timezone && (
                              <>
                                <Text>•</Text>
                                <Text fontSize="10px">{formatTimezone(chart.timeInfo.timezone)}</Text>
                              </>
                            )}
                          </HStack>
                          {chart.coordinates && (
                            <Text fontSize="10px">
                              {formatCoordinateDMS(chart.coordinates.longitude, false)}, {formatCoordinateDMS(chart.coordinates.latitude, true)} 
                              Lat:{chart.coordinates.latitude.toFixed(6)} Lon:{chart.coordinates.longitude.toFixed(6)}
                            </Text>
                          )}
                        </VStack>
                      </VStack>

                      <HStack spacing={1}>
                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={<DownloadIcon />}
                          onClick={() => handleLoadChart(chart)}
                          isDisabled={isLoading}
                          aria-label="Load chart"
                          w={4}
                          h={4}
                          minW={4}
                          color="#666"
                        />

                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={<StarIcon />}
                          color={chart.isFavorite ? '#ffc107' : '#ccc'}
                          onClick={() => handleToggleFavorite(chart.chartId)}
                          aria-label="Toggle favorite"
                          w={4}
                          h={4}
                          minW={4}
                        />

                        <IconButton
                          size="xs"
                          variant="ghost"
                          icon={<DeleteIcon />}
                          color="#d32f2f"
                          onClick={() => handleDeleteChart(chart.chartId)}
                          aria-label="Delete chart"
                          w={4}
                          h={4}
                          minW={4}
                        />
                      </HStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>

        {/* Footer */}
        <HStack justify="space-between" mt={3} pt={2} borderTop="1px solid #e0e0e0">
          <Text fontSize="14px" color="#666">
            {filteredCharts.length} of {recentCharts.length} charts
          </Text>
          
          <Button size="xs" variant="outline" onClick={loadRecentCharts} isLoading={isLoading} fontSize="14px" borderColor="#e5e5e5">
            Refresh
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

export default ChartManagement;